import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { roleService } from '../services/api';
import api from '../services/api';
import { Card, Input, Button, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';

const CreateEditRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();

  const [roleName, setRoleName] = useState('');
  const [pages, setPages] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roleNameError, setRoleNameError] = useState('');
  const [permissionError, setPermissionError] = useState('');
  const [expandedParents, setExpandedParents] = useState({});

  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    setPageTitle(isViewMode ? 'View Role' : isEditMode ? 'Edit Role' : 'Add Role');
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    fetchPages();
    if (isEditMode || isViewMode) {
      fetchRoleData();
    }
  }, [id, isEditMode, isViewMode]);

  const fetchPages = async () => {
    try {
      const response = await api.get('/roles/pages/list');
      const pagesData = response.data || [];
      setPages(pagesData);
      // Auto-expand all parents in view mode
      if (isViewMode) {
        const parentIds = {};
        pagesData.forEach(p => {
          if (p.parent_id === null || p.parent_id === undefined) {
            const hasChildren = pagesData.some(c => c.parent_id === p.id);
            if (hasChildren) parentIds[p.id] = true;
          }
        });
        setExpandedParents(parentIds);
      }
    } catch (err) {
      console.error('Failed to fetch pages:', err);
    }
  };

  const fetchRoleData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/roles/${id}`);
      const role = response.data;
      setRoleName(role.role_name || '');
      setPermissions((role.permissions || []).map((p) => ({
        page_id: p.page_id,
        can_list: !!p.can_list,
        can_view: !!p.can_view,
        can_add: !!p.can_add,
        can_edit: !!p.can_edit,
        can_delete: !!p.can_delete,
        can_download: !!p.can_download
      })));
    } catch (err) {
      toast.error('Failed to fetch role details');
      navigate('/roles');
    } finally {
      setLoading(false);
    }
  };

  const groupedPages = useMemo(() => {
    const parents = pages
      .filter(page => page.parent_id === null || page.parent_id === undefined)
      .map(page => ({ ...page, children: [] }));

    pages.forEach(page => {
      if (page.parent_id) {
        const parent = parents.find(p => p.id === page.parent_id);
        if (parent) parent.children.push(page);
      }
    });

    return parents;
  }, [pages]);

  const toggleParent = (parentId) => {
    setExpandedParents(prev => ({
      ...prev,
      [parentId]: !prev[parentId]
    }));
  };

  const getPermissionValue = (pageId, permissionType) => {
    const perm = permissions.find(p => p.page_id === pageId);
    return perm ? !!perm[permissionType] : false;
  };

  const hasAnyPermissionSelected = (permission) => Boolean(
    permission?.can_list ||
    permission?.can_view ||
    permission?.can_add ||
    permission?.can_edit ||
    permission?.can_delete ||
    permission?.can_download
  );

  const renderPermissionCell = (page, permissionType, label) => {
    const isSupported = Boolean(page?.[permissionType]);
    const isChecked = getPermissionValue(page.id, permissionType);
    const isDisabled = loading || isViewMode || !isSupported;

    return (
      <label
        className={`inline-flex items-center justify-center w-full ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        title={isSupported ? label : `${label} not supported for this page`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => handlePermissionChange(page.id, permissionType, e.target.checked)}
          disabled={isDisabled}
          className={`h-4 w-4 rounded border-primary-300 accent-primary-600 focus:ring-primary-500 ${!isSupported ? 'opacity-40 cursor-not-allowed' : ''} disabled:cursor-not-allowed`}
        />
      </label>
    );
  };

  // Get all assignable pages (non-parent pages + standalone pages)
  const allAssignablePages = useMemo(() => {
    const result = [];
    groupedPages.forEach(page => {
      if (page.children?.length > 0) {
        page.children.forEach(child => result.push(child));
      } else {
        result.push(page);
      }
    });
    return result;
  }, [groupedPages]);

  // Check if all supported pages have a given permission
  const isColumnAllChecked = (permissionType) => {
    const supported = allAssignablePages.filter(p => Boolean(p[permissionType]));
    if (supported.length === 0) return false;
    return supported.every(p => getPermissionValue(p.id, permissionType));
  };

  // Toggle all supported pages for a permission type
  const handleColumnSelectAll = (permissionType) => {
    const allChecked = isColumnAllChecked(permissionType);
    const newValue = !allChecked;
    setPermissions(prev => {
      let updated = [...prev];
      allAssignablePages.forEach(page => {
        if (!page[permissionType]) return;
        const existing = updated.find(p => p.page_id === page.id);
        if (existing) {
          updated = updated.map(p => p.page_id === page.id ? { ...p, [permissionType]: newValue } : p);
        } else {
          updated.push({
            page_id: page.id,
            can_list: permissionType === 'can_list' ? newValue : false,
            can_view: permissionType === 'can_view' ? newValue : false,
            can_add: permissionType === 'can_add' ? newValue : false,
            can_edit: permissionType === 'can_edit' ? newValue : false,
            can_delete: permissionType === 'can_delete' ? newValue : false,
            can_download: permissionType === 'can_download' ? newValue : false
          });
        }
      });
      return updated;
    });
  };

  // Check if all supported permissions for a page row are checked
  const isRowAllChecked = (page) => {
    const types = ['can_list', 'can_view', 'can_add', 'can_edit', 'can_delete', 'can_download'];
    const supported = types.filter(t => Boolean(page[t]));
    if (supported.length === 0) return false;
    return supported.every(t => getPermissionValue(page.id, t));
  };

  // Toggle all permissions for a single page row
  const handleRowSelectAll = (page) => {
    const allChecked = isRowAllChecked(page);
    const newValue = !allChecked;
    const types = ['can_list', 'can_view', 'can_add', 'can_edit', 'can_delete', 'can_download'];
    setPermissions(prev => {
      const existing = prev.find(p => p.page_id === page.id);
      const newPerm = {};
      types.forEach(t => {
        newPerm[t] = page[t] ? newValue : false;
      });
      if (existing) {
        return prev.map(p => p.page_id === page.id ? { ...p, ...newPerm } : p);
      }
      return [...prev, { page_id: page.id, ...newPerm }];
    });
  };

  const handlePermissionChange = (pageId, permissionType, value) => {
    setPermissions(prev => {
      const existing = prev.find(p => p.page_id === pageId);
      if (existing) {
        return prev.map(p => p.page_id === pageId ? { ...p, [permissionType]: value } : p);
      }

      return [...prev, {
        page_id: pageId,
        can_list: permissionType === 'can_list' ? value : false,
        can_view: permissionType === 'can_view' ? value : false,
        can_add: permissionType === 'can_add' ? value : false,
        can_edit: permissionType === 'can_edit' ? value : false,
        can_delete: permissionType === 'can_delete' ? value : false,
        can_download: permissionType === 'can_download' ? value : false
      }];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRoleNameError('');
    setPermissionError('');

    if (!roleName.trim()) {
      setRoleNameError('Role name is required');
      return;
    }

    const selectedPermissions = permissions.filter(hasAnyPermissionSelected);
    if (selectedPermissions.length === 0) {
      setPermissionError('At least one permission must be selected');
      return;
    }

    // Check mandatory: either Ticket or Dashboard page must be selected
    const selectedPageIds = selectedPermissions.map(p => p.page_id);
    const ticketsPage = pages.find(p => p.page_route === '/tickets');
    const dashboardPage = pages.find(p => p.page_route === '/dashboard');
    const hasTicketsSelected = ticketsPage && selectedPageIds.includes(ticketsPage.id);
    const hasDashboardSelected = dashboardPage && selectedPageIds.includes(dashboardPage.id);

    if (!hasTicketsSelected && !hasDashboardSelected) {
      setPermissionError('Either Ticket or Dashboard page permission is mandatory');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        role_name: roleName,
        permissions: selectedPermissions
      };

      if (isEditMode) {
        await roleService.update(id, payload);
        toast.success('Role updated successfully');
      } else {
        await roleService.create(payload);
        toast.success('Role created successfully');
      }

      navigate('/roles');
    } catch (err) {
      const message = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} role`;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/roles');
  };

  if (loading && (isEditMode || isViewMode)) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading role details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full max-w-none">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="w-full">
          <Breadcrumbs
            items={[
              { label: 'Roles', to: '/roles' },
              { label: isViewMode ? 'View Role' : isEditMode ? 'Edit Role' : 'Add Role' }
            ]}
            className="mb-6"
          />

          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            <div className="flex-1 min-w-0">
              <Input
                label="Role Name"
                name="roleName"
                value={roleName}
                onChange={(e) => {
                  setRoleName(e.target.value);
                  if (roleNameError) setRoleNameError('');
                }}
                placeholder="Enter role name"
                required
                error={roleNameError}
                disabled={loading || isViewMode}
                readOnly={isViewMode}
              />
            </div>
          </div>

          <div className="w-full rounded-xl border border-primary-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-primary-50 border-b border-primary-100">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Page Permissions</h3>
                {permissionError && (
                  <p className="text-sm text-danger-600 mt-1">{permissionError}</p>
                )}
              </div>
            </div>
            <div className="w-full overflow-x-auto">
              <table className="min-w-full w-full divide-y divide-primary-100">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-primary-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-primary-700">Page</th>
                    {['can_list', 'can_view', 'can_add', 'can_edit', 'can_delete', 'can_download'].map(type => {
                      const label = type.replace('can_', '').charAt(0).toUpperCase() + type.replace('can_', '').slice(1);
                      return (
                        <th key={type} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-primary-700">
                          <div className="flex flex-col items-center gap-1">
                            <span>{label}</span>
                            <input
                              type="checkbox"
                              checked={isColumnAllChecked(type)}
                              onChange={() => handleColumnSelectAll(type)}
                              disabled={loading || isViewMode}
                              title={`Select all ${label}`}
                              className={`h-3.5 w-3.5 rounded border-primary-300 accent-primary-600 focus:ring-primary-500 ${isViewMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            />
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary-50">
                  {groupedPages.map(page => (
                    <React.Fragment key={page.id}>
                      <tr className={`${page.children?.length > 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-primary-50/60 transition-colors`}>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap" colSpan={page.children?.length > 0 ? 7 : 1}>
                          {page.children?.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => toggleParent(page.id)}
                              className="inline-flex items-center gap-2 text-left hover:text-primary-600 transition-colors"
                              aria-expanded={!!expandedParents[page.id]}
                            >
                              <svg
                                className={`w-4 h-4 text-primary-600 transition-transform duration-200 ${expandedParents[page.id] ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              <span>{page.page_name}</span>
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isRowAllChecked(page)}
                                onChange={() => handleRowSelectAll(page)}
                                disabled={loading || isViewMode}
                                title="Select all permissions for this page"
                                className={`h-4 w-4 rounded border-primary-300 accent-primary-600 focus:ring-primary-500 ${isViewMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                              />
                              <span>{page.page_name}</span>
                            </span>
                          )}
                        </td>
                        {!page.children?.length && (
                          <>
                            <td className="px-4 py-3 text-center">{renderPermissionCell(page, 'can_list', 'List')}</td>
                            <td className="px-4 py-3 text-center">{renderPermissionCell(page, 'can_view', 'View')}</td>
                            <td className="px-4 py-3 text-center">{renderPermissionCell(page, 'can_add', 'Add')}</td>
                            <td className="px-4 py-3 text-center">{renderPermissionCell(page, 'can_edit', 'Edit')}</td>
                            <td className="px-4 py-3 text-center">{renderPermissionCell(page, 'can_delete', 'Delete')}</td>
                            <td className="px-4 py-3 text-center">{renderPermissionCell(page, 'can_download', 'Download')}</td>
                          </>
                        )}
                      </tr>
                      {expandedParents[page.id] && page.children && page.children.map(child => (
                        <tr key={child.id} className="bg-primary-50/30 hover:bg-primary-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap pl-10">
                            <span className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isRowAllChecked(child)}
                                onChange={() => handleRowSelectAll(child)}
                                disabled={loading || isViewMode}
                                title="Select all permissions for this page"
                                className={`h-4 w-4 rounded border-primary-300 accent-primary-600 focus:ring-primary-500 ${isViewMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                              />
                              <span>→ {child.page_name}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">{renderPermissionCell(child, 'can_list', 'List')}</td>
                          <td className="px-4 py-3 text-center">{renderPermissionCell(child, 'can_view', 'View')}</td>
                          <td className="px-4 py-3 text-center">{renderPermissionCell(child, 'can_add', 'Add')}</td>
                          <td className="px-4 py-3 text-center">{renderPermissionCell(child, 'can_edit', 'Edit')}</td>
                          <td className="px-4 py-3 text-center">{renderPermissionCell(child, 'can_delete', 'Delete')}</td>
                          <td className="px-4 py-3 text-center">{renderPermissionCell(child, 'can_download', 'Download')}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={loading} className="border-primary-600 text-primary-600 hover:bg-primary-50">
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving...' : isEditMode ? 'Update Role' : '+ Add Role'}
              </Button>
            )}
            {isViewMode && (
              <Button type="button" variant="primary" onClick={() => navigate(`/roles/edit/${id}`)}>
                Edit Role
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateEditRole;
